import type { DocumentSignatureErrorKind } from "@interfaces";
import { DocumentSignatureError, findBlockingTimeInSeconds } from "@interfaces";
import type { AxiosError } from "axios";

export type ErrorType = {
  error: { name: string; message: string };
  message: string;
};

// Error names the back sends, per provider. A new provider only adds rows here.
const SIGNATURE_ERROR_KIND_BY_NAME = new Map<string, DocumentSignatureErrorKind>([
  ["InvalidPinEsignError", "invalidPin"],
  ["BlockedSignatureEsignError", "blockedPin"],
  ["EsignError", "providerError"],
  ["InvalidPinBpoError", "invalidPin"],
  ["BlockedSignatureBpoError", "blockedPin"],
  ["CertificateRevokedBpoError", "certificateRevoked"],
  ["BpoError", "providerError"],
  ["SignedHashTimeoutBpoError", "providerError"],
  ["CmsTooLargeBpoError", "providerError"],
  ["MissingBpoSignerGuidError", "providerError"],
  ["ValidationDeadlinePassedError", "validationRequired"],
]);

export type DocumentSignatureErrorCallbacks = {
  onInvalidPinError?: () => void;
  onBlockedPinError?: (errorMessage: string) => void;
  onEsignError?: () => void;
  onUnknownError?: () => void;
  onValidationRequiredError?: () => void;
  onCertificateRevokedError?: () => void;
};

// The back's message for a BPO block that BPO did not describe
// (`mekidoc-back/services/bpo/signSignature.ts`). It is not the provider's words,
// so the doctor never sees it.
const BLOCKED_SIGNATURE_PLACEHOLDER = "Blocked signature";

export type BlockedPinDetails = {
  // Null when the provider gave no waiting time: the modal must not invent one.
  blockingTimeInSeconds: number | null;
  // The provider's own text about the block, only when it gave no time: with a
  // time, the countdown already says it.
  providerText: string | null;
};

export const blockedPinDetails = (message: string): BlockedPinDetails => {
  const text = message.trim();
  const blockingTimeInSeconds = findBlockingTimeInSeconds(text);
  const isProviderText = !!text && text !== BLOCKED_SIGNATURE_PLACEHOLDER;

  return {
    blockingTimeInSeconds,
    providerText: blockingTimeInSeconds === null && isProviderText ? text : null,
  };
};

export const onDocumentSignatureAxiosErrorInterpreter = (
  error: AxiosError<ErrorType>,
) => {
  const status = error.response?.status;
  const name = error.response?.data?.error?.name;

  const message = error.response?.data?.message ? "[message present]" : undefined;

  console.error("Error when signing documents:", { status, name, message });

  const kind = name ? SIGNATURE_ERROR_KIND_BY_NAME.get(name) : undefined;

  if (kind) {
    throw new DocumentSignatureError(kind, error.response?.data.error?.message ?? "");
  }

  throw error;
};

export const dispatchDocumentSignatureError = (
  error: Error,
  {
    onInvalidPinError,
    onBlockedPinError,
    onEsignError,
    onUnknownError,
    onValidationRequiredError,
    onCertificateRevokedError,
  }: DocumentSignatureErrorCallbacks,
) => {
  if (!(error instanceof DocumentSignatureError)) {
    onUnknownError?.();

    return;
  }

  const handlersByKind: Record<DocumentSignatureErrorKind, () => void> = {
    invalidPin: () => onInvalidPinError?.(),
    blockedPin: () => onBlockedPinError?.(error.message),
    providerError: () => onEsignError?.(),
    validationRequired: () => onValidationRequiredError?.(),
    certificateRevoked: () => onCertificateRevokedError?.(),
  };

  handlersByKind[error.kind]();
};
